import React, { useState, useMemo, useCallback } from 'react';
import usePlants from '../hooks/usePlants';
import PlantCard from './PlantCard';
import WetlandRatingSelect from './WetlandRatingSelect';
import './SearchView.css';

function SearchView({ onViewPlant }) {
  const { plants, species, deleteSpecies } = usePlants();
  const [query, setQuery] = useState('');
  const [nurseryOnly, setNurseryOnly] = useState(false);
  const [filters, setFilters] = useState({
    wetlandRating: null,
    light: null,
    moisture: null,
    bloomSeason: null,
    rootType: null,
    floodTolerance: null,
    application: null,
    tidalTolerance: null,
    droughtTolerance: null,
  });
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Build a set of species names that exist in the nursery (have plant instances)
  const nurserySpeciesKeys = useMemo(() => {
    const keys = new Set();
    for (const p of plants) {
      keys.add(`${p.commonName}|||${p.scientificName}`.toLowerCase());
    }
    return keys;
  }, [plants]);

  // Build aggregated plant counts per species
  const plantCountsBySpecies = useMemo(() => {
    const counts = new Map();
    for (const p of plants) {
      const key = `${p.commonName}|||${p.scientificName}`.toLowerCase();
      const total = (p.qtyPlugs || 0) + (p.qtySoloCups || 0) + (p.qtyQuarts || 0) + (p.qtyGallons || 0);
      counts.set(key, (counts.get(key) || 0) + total);
    }
    return counts;
  }, [plants]);

  // Filter and search species
  const results = useMemo(() => {
    let list = species;

    // Text search
    if (query.trim()) {
      const lower = query.toLowerCase();
      list = list.filter(s =>
        (s.commonName || '').toLowerCase().includes(lower) ||
        (s.scientificName || '').toLowerCase().includes(lower)
      );
    }

    // Nursery only filter
    if (nurseryOnly) {
      list = list.filter(s => {
        const key = `${s.commonName}|||${s.scientificName}`.toLowerCase();
        return nurserySpeciesKeys.has(key);
      });
    }

    // Attribute filters
    if (filters.wetlandRating) {
      const rating = filters.wetlandRating.toLowerCase();
      list = list.filter(s =>
        (s.agcpRating || '').toLowerCase().includes(rating) ||
        (s.empRating || '').toLowerCase().includes(rating)
      );
    }
    if (filters.light) {
      const light = filters.light.toLowerCase();
      list = list.filter(s => (s.lightRequirements || '').toLowerCase().includes(light));
    }
    if (filters.moisture) {
      const moisture = filters.moisture.toLowerCase();
      list = list.filter(s => (s.moistureRequirements || '').toLowerCase().includes(moisture));
    }
    if (filters.bloomSeason) {
      const bloom = filters.bloomSeason.toLowerCase();
      list = list.filter(s => (s.bloomSeason || '').toLowerCase().includes(bloom));
    }
    if (filters.rootType) {
      list = list.filter(s => s.rootType === filters.rootType);
    }
    if (filters.floodTolerance) {
      list = list.filter(s => s.floodTolerance === filters.floodTolerance);
    }
    if (filters.tidalTolerance) {
      list = list.filter(s => s.tidalTolerance === filters.tidalTolerance);
    }
    if (filters.droughtTolerance) {
      list = list.filter(s => s.droughtTolerance === filters.droughtTolerance);
    }
    if (filters.application) {
      list = list.filter(s => (s.applications || []).includes(filters.application));
    }

    // Sort alphabetically by common name
    return list.sort((a, b) => (a.commonName || '').localeCompare(b.commonName || ''));
  }, [species, query, nurseryOnly, nurserySpeciesKeys, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value || null }));
  };

  const handleDelete = useCallback(async (speciesItem) => {
    await deleteSpecies(speciesItem.id);
    setConfirmDelete(null);
  }, [deleteSpecies]);

  return (
    <div className="search-view">
      <h2 className="search-view__title">Search Plants</h2>

      <div className="search-view__controls">
        <div className="search-view__search-field">
          <label htmlFor="search-input">Search</label>
          <input
            id="search-input"
            type="text"
            className="search-view__input"
            placeholder="Search by common or scientific name..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search plants by name"
          />
        </div>

        <div className="search-view__toggle-row">
          <label className="search-view__toggle">
            <input
              type="checkbox"
              checked={nurseryOnly}
              onChange={(e) => setNurseryOnly(e.target.checked)}
            />
            In nursery only
          </label>
        </div>

        <div className="search-view__filters">
          <WetlandRatingSelect
            id="filter-wetland"
            label="Wetland Rating"
            value={filters.wetlandRating}
            onChange={(val) => handleFilterChange('wetlandRating', val)}
          />
          <div className="search-view__filter-field">
            <label htmlFor="filter-light">Light</label>
            <select id="filter-light" value={filters.light || ''} onChange={(e) => handleFilterChange('light', e.target.value)}>
              <option value="">All</option>
              <option value="full sun">Full Sun</option>
              <option value="part shade">Part Shade</option>
              <option value="shade">Shade</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-moisture">Moisture</label>
            <select id="filter-moisture" value={filters.moisture || ''} onChange={(e) => handleFilterChange('moisture', e.target.value)}>
              <option value="">All</option>
              <option value="dry">Dry</option>
              <option value="moist">Moist</option>
              <option value="wet">Wet</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-bloom">Bloom</label>
            <select id="filter-bloom" value={filters.bloomSeason || ''} onChange={(e) => handleFilterChange('bloomSeason', e.target.value)}>
              <option value="">All</option>
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
              <option value="winter">Winter</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-application">Application</label>
            <select id="filter-application" value={filters.application || ''} onChange={(e) => handleFilterChange('application', e.target.value)}>
              <option value="">All</option>
              <option value="stream bank">Stream Bank</option>
              <option value="rain garden">Rain Garden</option>
              <option value="slope stabilization">Slope Stabilization</option>
              <option value="bioswale">Bioswale</option>
              <option value="living shoreline">Living Shoreline</option>
              <option value="riparian buffer">Riparian Buffer</option>
              <option value="stormwater pond">Stormwater Pond</option>
              <option value="wetland restoration">Wetland Restoration</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-flood">Flood Tolerance</label>
            <select id="filter-flood" value={filters.floodTolerance || ''} onChange={(e) => handleFilterChange('floodTolerance', e.target.value)}>
              <option value="">All</option>
              <option value="none">None</option>
              <option value="brief">Brief (&lt;48hr)</option>
              <option value="moderate">Moderate (days)</option>
              <option value="extended">Extended (weeks)</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-tidal">Tidal</label>
            <select id="filter-tidal" value={filters.tidalTolerance || ''} onChange={(e) => handleFilterChange('tidalTolerance', e.target.value)}>
              <option value="">All</option>
              <option value="none">None</option>
              <option value="occasional splash">Occasional Splash</option>
              <option value="regular tidal">Regular Tidal</option>
              <option value="brackish submersion">Brackish Submersion</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-root">Root Type</label>
            <select id="filter-root" value={filters.rootType || ''} onChange={(e) => handleFilterChange('rootType', e.target.value)}>
              <option value="">All</option>
              <option value="fibrous">Fibrous</option>
              <option value="taproot">Taproot</option>
              <option value="rhizomatous">Rhizomatous</option>
              <option value="stoloniferous">Stoloniferous</option>
            </select>
          </div>
          <div className="search-view__filter-field">
            <label htmlFor="filter-drought">Drought</label>
            <select id="filter-drought" value={filters.droughtTolerance || ''} onChange={(e) => handleFilterChange('droughtTolerance', e.target.value)}>
              <option value="">All</option>
              <option value="none">None</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
      </div>

      <p className="search-view__count" role="status">
        {results.length} species{nurseryOnly ? ' in nursery' : ''}
      </p>

      <div className="search-view__results">
        {results.map((sp) => {
          const key = `${sp.commonName}|||${sp.scientificName}`.toLowerCase();
          const inNursery = nurserySpeciesKeys.has(key);
          const qty = plantCountsBySpecies.get(key) || 0;

          return (
            <div key={sp.id} className="search-view__species-row">
              <div
                className="search-view__species-info"
                onClick={() => onViewPlant && onViewPlant(sp)}
              >
                <strong>{sp.commonName || sp.scientificName}</strong>
                {sp.scientificName && sp.commonName && (
                  <span className="search-view__sci">{sp.scientificName}</span>
                )}
                {(sp.agcpRating || sp.empRating) && (
                  <span className="search-view__wetland">
                    {sp.agcpRating && `AGCP: ${sp.agcpRating}`}
                    {sp.agcpRating && sp.empRating && ' · '}
                    {sp.empRating && `EMP: ${sp.empRating}`}
                  </span>
                )}
                <div className="search-view__species-tags">
                  {sp.rootType && <span className="search-view__tag">{sp.rootType}</span>}
                  {sp.floodTolerance && <span className="search-view__tag">flood: {sp.floodTolerance}</span>}
                  {sp.applications?.length > 0 && sp.applications.map(a => (
                    <span key={a} className="search-view__tag search-view__tag--app">{a}</span>
                  ))}
                </div>
                {inNursery && (
                  <span className="search-view__nursery-badge">{qty} in nursery</span>
                )}
              </div>
              <button
                className="search-view__delete-btn"
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(sp); }}
                aria-label={`Delete ${sp.commonName}`}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <div className="search-view__confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="search-view__confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p>Delete <strong>{confirmDelete.commonName}</strong> from the species catalog?</p>
            {nurserySpeciesKeys.has(`${confirmDelete.commonName}|||${confirmDelete.scientificName}`.toLowerCase()) && (
              <p className="search-view__confirm-warning">
                ⚠ This species has plant instances in the nursery. They won't be deleted but will lose their species link.
              </p>
            )}
            <div className="search-view__confirm-actions">
              <button onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="search-view__confirm-delete" onClick={() => handleDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchView;
